import { useLayoutEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import cc from 'classcat';
import { isRtl } from 'utils/constants/app/rtl';
import { EIconSize } from 'utils/constants/app/ui';
import { FAVORITE_TEAMS_ALL_ROUT } from 'utils/constants/sportsbook/favorite-team';
import { fireEvent } from 'utils/generic/event-bus';
import { mountPoint } from 'utils/generic/route-manipulation';
import { isMobile } from 'utils/is-mobile';
import { ENotRealSports } from 'interfaces/not-real-sports';
import { TTeam } from 'interfaces/sportsbook-data-levels';
import { GlobalIcon } from 'components/GlobalIcon';
import { Button } from 'newcomponents/UI/Button';
import { SwiperSlider } from 'newcomponents/UI/SwiperSlider';
import { useCustomHistory } from 'newhooks/generic/useCustomHistory';
import { useSbRouteMatch } from 'newhooks/generic/useSbRouteMatch';
import { resetMarketTypes } from 'store/actions';
import { setSportsbookSelectedSport } from 'store/actions/new-sportsbook';
import { getFavoriteSportTeams } from 'store/selectors/fav-data';
import { getIsLoggedIn, getUser } from 'store/selectors/user-data';
import { FavoriteTeamSingleView } from '../FavoriteTeamSingleView';
import './index.less';

if (isRtl) {
  import('./rtl.less');
}

export const FavoriteTeamsHeader = () => {
  const favoriteTeams: TTeam[] = useSelector(getFavoriteSportTeams);
  const { t } = useTranslation();
  const { goBack, push } = useCustomHistory();
  const isLoggedIn = useSelector(getIsLoggedIn);
  const user = useSelector(getUser);
  const dispatch = useDispatch();

  const history = useHistory();
  const location = useLocation();

  const handleRedirect = () => {
    const redirectUrl = `${mountPoint(location.pathname)}/${
      ENotRealSports.team
    }/${FAVORITE_TEAMS_ALL_ROUT}`;

    if (
      location.pathname.includes(
        `${mountPoint(location.pathname)}/${ENotRealSports.team}`
      )
    ) {
      history.push(redirectUrl);
    } else {
      push(redirectUrl);
    }
  };

  const routeMatch = useSbRouteMatch(
    `${mountPoint(location.pathname)}/${ENotRealSports.team}/:sport`
  );

  const isActive = routeMatch.params.sport === FAVORITE_TEAMS_ALL_ROUT;

  useLayoutEffect(() => {
    if (!isLoggedIn && !user.pending) {
      goBack(mountPoint(location.pathname, false));
    }
  }, [isLoggedIn, user.pending]);

  return (
    <div
      className={cc([
        'favoriteTeamsHeader',
        {
          'favoriteTeamsHeader--mobile': isMobile()
        }
      ])}
    >
      <div className="favoriteTeamsHeader__section">
        <div className="favoriteTeamsHeader__section__leftBlock">
          <div
            className="favoriteTeamsHeader__section__leftBlock__backButton"
            onClick={() => {
              goBack(mountPoint(location.pathname, false));
              dispatch(setSportsbookSelectedSport(null));
              dispatch(resetMarketTypes());
            }}
          >
            <GlobalIcon
              lib="generic"
              name="caretleft"
              theme="default"
              size={EIconSize._10}
            />
          </div>
          <div className="favoriteTeamsHeader__section__leftBlock__title">
            {t('sportsbook.myTeams')}
          </div>
        </div>
        <div className="favoriteTeamsHeader__section__rigthBlock">
          <Button
            type="secondary"
            className="favoriteTeamsHeader__section__rigthBlock__button"
            onClick={() => {
              fireEvent({
                key: 'openFavoriteTeamPopup'
              });
            }}
          >
            <GlobalIcon
              lib="generic"
              name="edit"
              theme="default"
              size={EIconSize._20}
            />
            <div className="favoriteTeamsHeader__section__rigthBlock__button__text">
              {t('sportsbook.edit')}
            </div>
          </Button>
        </div>
      </div>
      <div className="favoriteTeamsHeader__main">
        <SwiperSlider
          bodyArrows={!isMobile()}
          activeSlide={0}
          style={{ width: '100%' }}
          spaceBetween={12}
          drawableTabs={[
            <div
              key={-1}
              data-testid="all"
              className={cc([
                'favoriteTeamsHeader__main__all',
                {
                  'favoriteTeamsHeader__main__all--active': isActive
                }
              ])}
              onClick={handleRedirect}
            >
              {t('sportsbook.all')}
            </div>,
            ...favoriteTeams.map(team => {
              return (
                <div
                  data-testid={team.teamName?.toLowerCase()}
                  className="favoriteTeamsHeader__main__teams"
                  key={team.teamId}
                >
                  <FavoriteTeamSingleView
                    teamId={team.teamId}
                    teamName={team.teamName}
                    sport={team.sport}
                    withWidth
                  />
                </div>
              );
            })
          ]}
        />
      </div>
    </div>
  );
};
